/**
 * RelationshipDialog Component
 *
 * Dialog for creating and editing linked table relationships (joins).
 * Uses paired field mapping rows for intuitive condition building.
 *
 * @module RelationshipDialog
 */

import { useMemo, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Link2, GitMerge, Loader2 } from 'lucide-react';
import type {
  TableFieldApiResponse,
  LinkedTableCalculatedColumnApi,
} from '@loader/shared-types';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@loader/ui';

import { TableExplorer } from '@/features/linkedTables/shared/components/TableExplorer';
import { isFieldMappingComplete } from '@/features/linkedTables/editLinkedTable/utils/joinConditionMapper';
import { getFieldLabel } from '@/features/linkedTables/editLinkedTable/components/FieldCombobox';
import { FieldMappingRow } from '@/features/linkedTables/editLinkedTable/components/FieldMappingRow';
import type { SchemaColumn } from '@/features/linkedTables/editLinkedTable/formula/operatorConstants';
import type { LinkedTableRelationship, FieldMapping } from '@/lib/types';
import { useRpc } from '@/lib/rpc';
import { queryKeys } from '@/lib/queryKeys';
import { cn } from '@/lib/utils';

interface RelationshipDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  relationship: LinkedTableRelationship | null;
  availableFields: TableFieldApiResponse[];
  currentLinkedTableId?: string;
  currentTableId?: string;
  primaryTableAlias?: string;
  onSave: (relationship: LinkedTableRelationship) => void;
  isSaving?: boolean;
}

type JoinType = 'LEFT' | 'INNER' | 'RIGHT' | 'FULL';

/**
 * Dialog for creating and editing relationships
 */
export function RelationshipDialog({
  open,
  onOpenChange,
  relationship,
  availableFields,
  currentLinkedTableId,
  currentTableId,
  primaryTableAlias,
  onSave,
  isSaving = false,
}: RelationshipDialogProps) {
  const rpc = useRpc();
  const isEditing = !!relationship;

  // Local state
  const [targetId, setTargetId] = useState('');
  const [targetType, setTargetType] = useState<'table' | 'linked_table'>('table');
  const [targetName, setTargetName] = useState('');
  const [alias, setAlias] = useState('');
  const [joinType, setJoinType] = useState<JoinType>('LEFT');
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      if (relationship) {
        setTargetId(relationship.target_id);
        setTargetType(relationship.target_type);
        setTargetName(relationship.target_name || '');
        setAlias(relationship.alias);
        setJoinType(relationship.join_type.toUpperCase() as JoinType);
        setFieldMappings(
          relationship.field_mappings?.length
            ? relationship.field_mappings
            : [{ source_field_id: '', target_field_id: '' }]
        );
      } else {
        setTargetId('');
        setTargetType('table');
        setTargetName('');
        setAlias('');
        setJoinType('LEFT');
        setFieldMappings([{ source_field_id: '', target_field_id: '' }]);
      }
    }
  }, [open, relationship]);

  // IDs to exclude from selection
  const excludeIds = useMemo(() => {
    const ids: string[] = [];
    if (currentTableId) ids.push(currentTableId);
    if (currentLinkedTableId) ids.push(currentLinkedTableId);
    return ids;
  }, [currentTableId, currentLinkedTableId]);

  // Fetch target fields
  const { data: targetFieldsData, isLoading: loadingTargetFields } = useQuery({
    queryKey: queryKeys.linkedTables.targetFields(targetId || '', targetType || ''),
    queryFn: async () => {
      if (!targetId) return [];

      if (targetType === 'table') {
        // @ts-expect-error - Hono RPC type inference limitation
        const res = await rpc.api.private.tables[':id'].$get({
          param: { id: targetId },
        });

        if (!res.ok) throw new Error('Failed to fetch table');
        const tableResponse = await res.json();
        return tableResponse.table?.fields || [];
      }

      // @ts-expect-error - Hono RPC type inference limitation
      const linkedTableRes = await rpc.api.private['linked-tables'][':id'].$get({
        param: { id: targetId },
      });

      if (!linkedTableRes.ok) throw new Error('Failed to fetch linked table');
      const linkedTableResponse = await linkedTableRes.json();
      const linkedTable = linkedTableResponse.linkedTable;

      // @ts-expect-error - Hono RPC type inference limitation
      const tableRes = await rpc.api.private.tables[':id'].$get({
        param: { id: linkedTable.table_id },
      });
      if (!tableRes.ok) throw new Error('Failed to fetch linked table base table');
      const tableResponse = await tableRes.json();

      const baseFields = tableResponse.table?.fields || [];
      const calculatedColumns = linkedTable.calculated_columns || [];

      const calculatedFields: TableFieldApiResponse[] = calculatedColumns.map(
        (calcCol: LinkedTableCalculatedColumnApi, index: number) => ({
          id: calcCol.id,
          table_id: linkedTable.table_id,
          name: calcCol.name,
          data_type: (calcCol.data_type || 'text') as TableFieldApiResponse['data_type'],
          is_required: false,
          is_unique: false,
          is_enabled: true,
          order_index: 1000 + index,
          description: null,
          default_value: null,
          validation_rules: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
      );

      return [...baseFields, ...calculatedFields];
    },
    enabled: open && !!targetId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const targetFields = useMemo<TableFieldApiResponse[]>(() => targetFieldsData || [], [targetFieldsData]);

  // Get source table alias (from existing relationship or primary table)
  const sourceTableAlias = relationship?.source_table_alias || primaryTableAlias || '';

  // Get source table name for display
  const sourceTableName = sourceTableAlias || 'base';

  /**
   * Filter available fields to only show fields from the source table.
   * Fields have a table_alias property that identifies which table they belong to.
   */
  const sourceFields = useMemo(() => {
    if (!sourceTableAlias) return availableFields;

    return availableFields.filter((field) => {
      const fieldAlias = field.table_alias || '';
      return fieldAlias === sourceTableAlias || fieldAlias === '';
    });
  }, [availableFields, sourceTableAlias]);

  // Handle target selection (only tables for now, linked tables support available via includeLinkedTables prop)
  const handleTargetChange = (newTargetId: string, newTargetName: string) => {
    setTargetId(newTargetId);
    setTargetType('table');
    setTargetName(newTargetName);

    // Always regenerate alias from the new table name so the join builder
    // labels and formula schema reflect the selected table.
    const baseAlias = newTargetName
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, '_')
      .replace(/^(\d)/, '_$1')
      .substring(0, 20);
    setAlias(baseAlias);

    // Reset to one empty mapping when target changes
    setFieldMappings([{ source_field_id: '', target_field_id: '' }]);
  };

  // Handle field mapping updates
  const handleMappingUpdate = (index: number, mapping: FieldMapping) => {
    const newMappings = [...fieldMappings];
    newMappings[index] = mapping;
    setFieldMappings(newMappings);
  };

  const handleMappingRemove = (index: number) => {
    const newMappings = fieldMappings.filter((_, i) => i !== index);
    setFieldMappings(newMappings);
  };

  const handleAddCondition = () => {
    setFieldMappings([...fieldMappings, { source_field_id: '', target_field_id: '' }]);
  };

  // Build schema columns from fields for Monaco editor
  const sourceSchema = useMemo<SchemaColumn[]>(
    () =>
      sourceFields.map((f) => ({
        id: f.name,
        label: getFieldLabel(f),
        data_type: f.data_type,
        tableAlias: sourceTableAlias,
      })),
    [sourceFields, sourceTableAlias]
  );

  const targetSchema = useMemo<SchemaColumn[]>(
    () =>
      targetFields.map((f) => ({
        id: f.name,
        label: getFieldLabel(f),
        data_type: f.data_type,
        tableAlias: alias || targetName || 'target',
      })),
    [targetFields, alias, targetName]
  );

  // Form validation
  const isValid =
    targetId &&
    alias &&
    fieldMappings.length > 0 &&
    fieldMappings.every(isFieldMappingComplete);

  // Handle save
  const handleSave = () => {
    if (!isValid) return;

    const result: LinkedTableRelationship = {
      id: relationship?.id || `rel-${Date.now()}`,
      source_table_alias: relationship?.source_table_alias || primaryTableAlias || '',
      target_id: targetId,
      target_type: targetType,
      target_name: targetName,
      alias,
      join_type: joinType,
      field_mappings: fieldMappings,
    };

    onSave(result);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Use inset-0 + margin:auto centering instead of translate(-50%,-50%) to avoid
          creating a CSS containing block that breaks Monaco's fixedOverflowWidgets */}
      <DialogContent
        className={cn(
          'sm:max-w-[700px]',
          '!top-0 !left-0 !right-0 !bottom-0 !m-auto !h-fit'
        )}
        style={{ translate: 'none' }}
      >
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Relationship' : 'Add Relationship'}
          </DialogTitle>
          <DialogDescription>
            Configure a join between tables. Select the target and map the fields.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Connection Section */}
          <div className="space-y-3">
            <h4 className="text-muted-foreground flex items-center gap-2 border-b pb-2 text-sm font-medium">
              <Link2 className="h-4 w-4" />
              Connection
            </h4>

            {/* Target Table */}
            <div className="space-y-2">
              <Label>Target Table</Label>
              <TableExplorer
                value={targetId}
                onValueChange={handleTargetChange}
                excludeIds={excludeIds}
              />
            </div>

            {/* Alias */}
            <div className="flex items-center gap-4">
              <Label htmlFor="alias" className="w-24 shrink-0 text-right">
                Alias
              </Label>
              <Input
                id="alias"
                value={alias}
                onChange={(e) => setAlias(e.target.value.replace(/[^a-zA-Z0-9_]/g, '_').replace(/^(\d)/, '_$1'))}
                placeholder="e.g., orders, customers"
                disabled={!targetId}
                className="flex-1"
              />
            </div>

            {/* Join Type */}
            <div className="flex items-center gap-4">
              <Label className="w-24 shrink-0 text-right">Join Type</Label>
              <Select
                value={joinType}
                onValueChange={(value) => setJoinType(value as JoinType)}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LEFT">LEFT (keep all from source)</SelectItem>
                  <SelectItem value="INNER">INNER (only matching)</SelectItem>
                  <SelectItem value="RIGHT">RIGHT (keep all from target)</SelectItem>
                  <SelectItem value="FULL">FULL (keep all from both)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Join Conditions Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between border-b pb-2">
              <h4 className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <GitMerge className="h-4 w-4" />
                Join Conditions
              </h4>
              {targetId && !loadingTargetFields && (
                <Button
                  variant="outline"
                  size="icon"
                  className="h-7 w-7"
                  onClick={handleAddCondition}
                  aria-label="Add join condition"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              )}
            </div>
            {targetId && !loadingTargetFields ? (
              <div className="space-y-2">
                {fieldMappings.map((mapping, index) => (
                  <FieldMappingRow
                    key={index}
                    mapping={mapping}
                    index={index}
                    sourceFields={sourceFields}
                    targetFields={targetFields}
                    sourceTableName={sourceTableName}
                    targetTableName={alias || targetName || 'target'}
                    sourceSchema={sourceSchema}
                    targetSchema={targetSchema}
                    onUpdate={handleMappingUpdate}
                    onRemove={handleMappingRemove}
                    canRemove={fieldMappings.length > 1}
                  />
                ))}
              </div>
            ) : targetId && loadingTargetFields ? (
              <div className="text-muted-foreground text-sm">Loading fields...</div>
            ) : (
              <div className="text-muted-foreground text-sm">
                Select a target table first
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" className="w-24" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button className="w-24" onClick={handleSave} disabled={!isValid || isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
